import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {rootState, UserSessionState} from '@mintdata-wsp/common/state/RootState';
import {ConfigUtil} from '@mintdata-wsp/common/util/ConfigUtil';
import {Category, Logger} from '@mintdata/mint-kit/api/log/mk';
import {reaction} from 'mobx';

const logger = Logger.getLogger(Category.System);

export interface ICaptureOptions
{
    tags?:{
        [tagName:string]:string | number | boolean,
    },
    extra?:{
        [tagName:string]:string | number | boolean,
    },
}

export class LogRocketController
{
    private _isInitializing:boolean = false;
    private _isInitialized:boolean = false;
    private _logRocketLoadPromise:Promise<any> = null;

    public async init():Promise<void>
    {
        if( this.isInitialized() || this._isInitializing )
            return;

        if( !this._isLogRocketEnabled() )
        {
            logger.info(`LogRocket is disabled`);
            return;
        }

        try
        {
            this._isInitializing = true;

            this._logRocketLoadPromise = import(/* webpackChunkName: "logrocket" */ 'logrocket').then((module) => module.default);
            const logRocket = await this._logRocketLoadPromise;

            const logRocketAccessKey = ConfigUtil.logRocketAccessKey();
            logger.debug(`LogRocket is enabled with key: ${logRocketAccessKey}`);

            logger.debug(`LogRocket::init`);

            const {version} = rootState.ui;

            logRocket.init(logRocketAccessKey, {
                release: version.isLocalDevMode ? 'LOCAL' : version.release || version.number,
                mergeIframes: true
            });

            reaction(() => rootState.userSession,
                userSession => this._identifySession(userSession),
                {fireImmediately: true, name: 'identifySession'});
        }
        catch( e )
        {
            console.error(e);
        }
        finally
        {
            this._isInitializing = false;
        }

        this._isInitialized = true;
    }

    public isInitialized():boolean
    {
        return this._isInitialized;
    }

    public async startNewSession():Promise<void>
    {
        if( !this._isLogRocketEnabled() )
            return;

        try
        {
            const logRocket = await this._logRocketLoadPromise;

            console.info(`LogRocket::start new session`);
            logRocket.startNewSession();

            await this._identifySession(rootState.userSession);
        }
        catch( e )
        {
            console.error(e);
        }
    }

    public async track(eventName:string):Promise<void>
    {
        if( !this._isLogRocketEnabled() || !eventName )
            return;

        try
        {
            const logRocket = await this._logRocketLoadPromise;

            logger.debug('LogRocket::track', eventName);
            logRocket.track(eventName);
        }
        catch( e )
        {
            console.error(e);
        }
    }

    public async captureMessage(message:string, options?:ICaptureOptions)
    {
        if( !this._isLogRocketEnabled() || !message )
            return;

        try
        {
            const logRocket = await this._logRocketLoadPromise;

            logger.debug('LogRocket::captureMessage', message, options);
            logRocket.captureMessage(message, options);
        }
        catch( e )
        {
            console.error(e);
        }
    }

    private async _identifySession(userSession:UserSessionState)
    {
        if( !this._isLogRocketEnabled() || !userSession )
            return;

        try
        {
            const logRocket = await this._logRocketLoadPromise;

            const currentUser = userSession.currentUser;

            if( currentUser && currentUser.email && currentUser.fullName )
            {
                const identity = {
                    name: currentUser.fullName,
                    email: currentUser.email
                };

                logger.debug(`LogRocket::identifyUserSession`, identity);
                logRocket.identify(currentUser.username, identity);
            }
        }
        catch( e )
        {
            console.error(e);
        }
    }

    private _isLogRocketEnabled():boolean
    {
        return ConfigUtil.isLogRocketEnabled() && !commonControllers.context.isStandaloneMode();
    }
}
